import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Printer, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { eventsApi } from '@/features/events/api';
import { despliegueDeEstacion, xurveyApi, xurveyPublicUrl } from '@/lib/api/xurvey';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  stationId: number;
  /** Optional — saves a fetch when the caller already has it. */
  stationName?: string;
  /**
   * El enlace de Xurvey, si quien abre el diálogo ya lo tiene.
   *
   * Normalmente no hace falta: el diálogo lo resuelve solo. Sirve para el caso
   * en que se acaba de elegir una encuesta distinta y todavía no está en la
   * caché — ahí manda lo que ve quien está eligiendo, no lo que hay guardado.
   */
  xurveyUrl?: string | null;
}

/**
 * The station QR, in context.
 *
 * Replaces the standalone `/stations/:id/qr` page, which existed only to hold
 * a QR and a Back button. Everything that page did — scan, download, print,
 * open — happens here without leaving the station.
 *
 * Self-contained on purpose: it resolves the station itself so every caller
 * (station header, station list, Distribute tab, operator view) is a one-liner
 * and none of them has to thread QR data through.
 */
export function StationQrDialog({
  open,
  onOpenChange,
  stationId,
  stationName,
  xurveyUrl,
}: Props) {
  const t = useT();
  const station = useQuery({
    queryKey: queryKeys.stations.detail(stationId),
    queryFn: () => eventsApi.getStation(stationId),
    // Only fetch once the dialog is actually opened.
    enabled: open && Number.isFinite(stationId),
  });

  const data = station.data;

  /*
   * El enlace del despliegue de esta estación.
   *
   * Se resuelve aquí y no lo pasa cada llamante: una estación desplegada se
   * sirve desde Xurvey, y si el QR llevara la dirección antigua, un cartón
   * impreso obligaría a pasar por Xotski para rebotar. Funciona —redirige—
   * pero deja el papel dependiendo de un salto que no necesita.
   *
   * `surveysId` es lo que hace esto posible: Xotski la anota al desplegar.
   */
  const despliegues = useQuery({
    queryKey: ['xurvey', 'deployments', data?.surveysId],
    queryFn: () => xurveyApi.listDeployments(data!.surveysId!),
    enabled: open && !!data?.surveysId && !xurveyUrl,
    staleTime: 60_000,
  });
  // El real y activo, nunca uno de prueba: ver `despliegueDeEstacion`.
  const slug = despliegueDeEstacion(despliegues.data, stationId)?.slug;
  const enlaceXurvey = xurveyUrl ?? (slug ? xurveyPublicUrl(slug) : null);
  // When the station belongs to a shared-QR group the printed code must be the
  // SHARED one (`/sg/:groupId`) — scanning it asks the guest to pick their
  // station first. Otherwise it's this station's own direct link.
  const shared = !!data?.qrGroupId && !enlaceXurvey;
  const shortUrl = enlaceXurvey
    ? enlaceXurvey
    : data
      ? shared
        ? `${window.location.origin}/sg/${data.qrGroupId}`
        : `${window.location.origin}/s/${data.surveysId}/${data.id}`
      : null;
  const longUrl = enlaceXurvey
    ? enlaceXurvey
    : data
      ? shared
        ? shortUrl
        : `${window.location.origin}/public-surveys?surveyId=${data.surveysId}&stationId=${data.id}`
      : null;

  const downloadSvg = () => {
    const svg = document.getElementById('xotski-qr')?.outerHTML;
    if (!svg) return;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `xotski-station-${stationId}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('stations.qrPrint.title')}
            {stationName || data?.name ? ` · ${stationName ?? data?.name}` : ''}
          </DialogTitle>
        </DialogHeader>

        {station.isLoading || despliegues.isLoading ? (
          <Skeleton className="h-72" />
        ) : !data?.surveysId && !enlaceXurvey ? (
          /* A deployed station needs no `surveysId`: its survey is Xurvey's,
             and Xotski's column stays empty. Checking only that column would
             claim "no survey" about a station that has a live link. */
          <p className="py-6 text-center text-sm text-[var(--color-fg-muted)]">
            {t('stations.qrPrint.noSurvey')}
          </p>
        ) : (
          <div className="space-y-4">
            {/* The white plate is required, not a theme slip: a QR needs a light
                quiet zone to scan reliably whatever the admin theme is. */}
            <div className="mx-auto grid h-60 w-60 place-items-center rounded-2xl bg-white p-5">
              <QRCodeSVG id="xotski-qr" value={shortUrl!} size={200} level="H" />
            </div>

            {shared && (
              <p className="rounded-lg border border-[var(--color-accent-ink)] bg-[var(--color-accent-10)] px-3 py-2 text-xs text-[var(--color-fg-primary)]">
                {t('stations.qrPrint.sharedNote')}
              </p>
            )}

            <div>
              <p className="text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
                {t('stations.qrPrint.shortUrl')}
              </p>
              <p className="break-all font-mono text-sm">{shortUrl}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
                {t('stations.qrPrint.fullUrl')}
              </p>
              <p className="break-all font-mono text-xs text-[var(--color-fg-muted)]">{longUrl}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button leftIcon={<Download className="h-4 w-4" />} onClick={downloadSvg}>
                {t('stations.qrPrint.downloadSvg')}
              </Button>
              <Button
                variant="outline"
                leftIcon={<Printer className="h-4 w-4" />}
                onClick={() => window.print()}
              >
                {t('stations.qrPrint.print')}
              </Button>
              <a href={shortUrl!} target="_blank" rel="noreferrer">
                <Button variant="ghost" leftIcon={<ExternalLink className="h-4 w-4" />}>
                  {t('stations.qrPrint.openNewTab')}
                </Button>
              </a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
